import {useCallback, useEffect, useMemo, useState} from 'react'
import {Interactions, Playlist} from '../common/types'
import {Api} from '../lib/api'
import {useSpotifyAuth} from './useSpotifyAuth'

export type RenderPlaylist = Playlist & {isSaved: boolean; voted: boolean}

const useInteractions = (): Interactions => {
  const [data, setData] = useState<Interactions>({saved: [], votes: []})
  const [auth] = useSpotifyAuth()

  useEffect(() => {
    if (auth?.authenticated) {
      return Api.subInteractions(setData)
    }
  }, [auth?.authenticated])

  return data
}

export const usePlaylists = (userId?: string) => {
  const [playlists, setPlaylists] = useState<Playlist[]>([])
  const interactions = useInteractions()

  useEffect(() => {
    return userId
      ? Api.subCollection(userId, setPlaylists)
      : Api.subPlaylists(setPlaylists)
  }, [userId])

  const render = useMemo(
    () =>
      playlists.map(p => ({
        ...p,
        isSaved: interactions.saved.includes(p.id),
        voted: interactions.votes.includes(p.id)
      })),
    [playlists, interactions]
  )

  return render
}

const PER_PAGE = 9

export const usePagedPlaylists = (userId?: string) => {
  const [paged, setPaged] = useState<RenderPlaylist[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)

  const playlists = usePlaylists(userId)

  useEffect(() => {
    if (playlists.length > 0) {
      console.debug('load more', pageNumber)
      const page = playlists.slice(
        0,
        Math.min(playlists.length, PER_PAGE * pageNumber)
      )
      setPaged(page)
      setHasMore(playlists.length > PER_PAGE * pageNumber)
    }
  }, [pageNumber, playlists])

  const loadMore = useCallback(() => {
    setPageNumber(p => p + 1)
  }, [])

  return [paged, {loadMore, hasMore, total: playlists.length}] as const
}
